import * as React from "react"

import { NoteMessageEvent } from "webmidi"

import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"

import { midiEvents } from "../../../../../../lib/midiEvents"
import { useAppSelector } from "../../../../../../redux"

export default function MidiControls({
  midiBinding,
  setMidiBinding,
}: {
  midiBinding: string
  setMidiBinding: (value: string) => void
}) {
  const currentMIDIDevice = useAppSelector((state) => state.currentMIDIDevice)

  const [listening, setListening] = React.useState(false)

  const handleMidi = React.useCallback(
    (e: NoteMessageEvent) => {
      if (listening) {
        const midiBinding =
          e.note.name + e.note.octave + (e.note.accidental || "")
        setMidiBinding(midiBinding)
        setListening(false)
      }
    },
    [listening, setMidiBinding]
  )

  React.useEffect(() => {
    if (currentMIDIDevice) {
      try {
        midiEvents.addListener(currentMIDIDevice, handleMidi)
      } catch (err) {
        console.error("Error creating listener for webmidi", err)
      }
    }

    return () => {
      midiEvents.removeListener(currentMIDIDevice, handleMidi) // no args should clear all?
    }
  }, [currentMIDIDevice, handleMidi])

  return (
    <Box>
      {!currentMIDIDevice && (
        <Stack sx={{ width: "100%", mb: 2 }} spacing={2}>
          <Alert severity="warning">Please select a MIDI device first.</Alert>
        </Stack>
      )}

      <Box sx={{ pb: 2 }}>
        <Typography variant="body1">
          Bind to a key on your MIDI device.
        </Typography>
      </Box>

      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        flexWrap="wrap"
      >
        {!listening && (
          <Button
            sx={{ mr: 2, mb: 1 }}
            variant="contained"
            onClick={() => setListening(true)}
          >
            Bind MIDI
          </Button>
        )}

        {listening && (
          <Button
            sx={{ mr: 2, mb: 1 }}
            variant="outlined"
            onClick={() => setListening(false)}
          >
            Listening...
          </Button>
        )}

        {midiBinding && (
          <Button
            sx={{ mr: 2, mb: 1 }}
            variant="outlined"
            onClick={() => {
              setListening(false)
              setMidiBinding("")
            }}
          >
            Clear MIDI
          </Button>
        )}

        {midiBinding && (
          <Typography sx={{ mb: 1 }} variant="body1">
            Binding: {midiBinding}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
